import React, { useEffect, useMemo, useRef, useState } from 'react';
import DayPicker from 'react-day-picker';
import { Dimmer, Loader } from 'semantic-ui-react';
import { CompactCalendarEvent, CompactMonthCalendarProps } from './types';
import {
    MONTHS,
    WEEKDAYS,
    WEEKDAYS_SHORT,
    getDateKey,
    getVisibleMonthRange,
    groupEventsByDateKey,
    shiftMonth,
    startOfDay,
} from './utils';
import {
    CalendarSmallIcon,
    CardChevronIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EmptyDayIcon,
} from '../../pages/calendar/CalendarIcons';
import './style.less';

const dayPickerClassNames = {
    ...((DayPicker as any).defaultProps && (DayPicker as any).defaultProps.classNames),
    container: 'DayPicker cmc-rdp',
    months: 'DayPicker-Months cmc-rdp-months',
    month: 'DayPicker-Month cmc-rdp-month',
    caption: 'cmc-rdp-hidden',
    navBar: 'cmc-rdp-hidden',
    weekdays: 'cmc-rdp-hidden',
    weekdaysRow: 'cmc-rdp-hidden',
    weekday: 'cmc-rdp-hidden',
    body: 'DayPicker-Body cmc-rdp-body',
    week: 'DayPicker-Week cmc-rdp-week',
    day: 'DayPicker-Day cmc-day',
    today: 'today',
    selected: 'selected',
    outside: 'other-month',
};

/** Day cell body — day number + up to three dots, then an overflow count. */
const CompactDayContent = ({
    date,
    eventsByDate,
}: {
    date: Date;
    eventsByDate: Map<string, CompactCalendarEvent[]>;
}) => {
    const dateKey = getDateKey(date);
    const dayEvents = eventsByDate.get(dateKey) || [];
    const count = dayEvents.length;
    const dotsToShow = Math.min(count, 3);

    return (
        <>
            <div className="cmc-day-num">{date.getDate()}</div>
            {count > 0 && (
                <div className="cmc-day-indicators">
                    <div className="cmc-dot-row">
                        {Array.from({ length: dotsToShow }).map((_, index) => {
                            const dotColor = dayEvents[index] && dayEvents[index].accentColor;
                            return (
                                <div
                                    key={`${dateKey}-dot-${index}`}
                                    className="cmc-dot"
                                    style={dotColor ? { background: dotColor } : undefined}
                                />
                            );
                        })}
                    </div>
                    {count > 3 && <div className="cmc-day-count">+{count - 3}</div>}
                </div>
            )}
        </>
    );
};

const CompactMonthCalendar = ({
    events,
    onDatesSet,
    onEventClick,
    initialMonth,
    loading = false,
    itemNoun = 'event',
    itemNounPlural,
}: CompactMonthCalendarProps) => {
    const today = useMemo(() => startOfDay(new Date()), []);
    const [currentYear, setCurrentYear] = useState(
        () => (initialMonth || today).getFullYear(),
    );
    const [currentMonth, setCurrentMonth] = useState(
        () => (initialMonth || today).getMonth(),
    );
    // Prefer today when it falls inside the month being restored; otherwise anchor to
    // the 1st so a month carried over from the other breakpoint still has a selection.
    const [selectedDate, setSelectedDate] = useState<Date>(() => {
        if (!initialMonth) return today;
        const sameMonth =
            initialMonth.getFullYear() === today.getFullYear() &&
            initialMonth.getMonth() === today.getMonth();
        return sameMonth
            ? today
            : new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1);
    });

    const visibleMonth = useMemo(
        () => new Date(currentYear, currentMonth, 1),
        [currentYear, currentMonth],
    );

    const visibleRange = useMemo(
        () => getVisibleMonthRange(currentYear, currentMonth),
        [currentYear, currentMonth],
    );

    const eventsByDate = useMemo(
        () => groupEventsByDateKey(events, visibleRange.start, visibleRange.end),
        [events, visibleRange.start, visibleRange.end],
    );

    const selectedKey = getDateKey(selectedDate);
    const todayKey = getDateKey(today);
    const selectedDayEvents = eventsByDate.get(selectedKey) || [];

    const lastEmittedRange = useRef<string>('');

    useEffect(() => {
        const key = `${visibleRange.start.getTime()}-${visibleRange.end.getTime()}`;
        if (lastEmittedRange.current === key) return;
        lastEmittedRange.current = key;
        onDatesSet({ start: visibleRange.start, end: visibleRange.end });
    }, [visibleRange.start, visibleRange.end, onDatesSet]);

    const changeMonth = (delta: number) => {
        const next = shiftMonth(currentYear, currentMonth, delta);
        setCurrentYear(next.year);
        setCurrentMonth(next.month);
        setSelectedDate(new Date(next.year, next.month, 1));
    };

    const handleToday = () => {
        const now = startOfDay(new Date());
        setCurrentYear(now.getFullYear());
        setCurrentMonth(now.getMonth());
        setSelectedDate(now);
    };

    const handleDayClick = (date: Date) => {
        const next = startOfDay(date);
        setSelectedDate(next);
        if (next.getMonth() !== currentMonth || next.getFullYear() !== currentYear) {
            setCurrentYear(next.getFullYear());
            setCurrentMonth(next.getMonth());
        }
    };

    const handleMonthChange = (month: Date) => {
        setCurrentYear(month.getFullYear());
        setCurrentMonth(month.getMonth());
    };

    const pluralNoun = itemNounPlural || `${itemNoun}s`;
    const countLabel =
        selectedDayEvents.length === 0
            ? `No ${pluralNoun}`
            : selectedDayEvents.length === 1
              ? `1 ${itemNoun}`
              : `${selectedDayEvents.length} ${pluralNoun}`;

    return (
        <div className="compact-month-calendar">
            <div className="cmc-month-card">
                <div className="cmc-month-row">
                    <div className="cmc-month-title">
                        {MONTHS[currentMonth]} <span>{currentYear}</span>
                    </div>
                    <div className="cmc-toolbar-actions">
                        <button type="button" className="cmc-today-btn" onClick={handleToday}>
                            Today
                        </button>
                        <div className="cmc-nav-arrows">
                            <button
                                type="button"
                                className="cmc-nav-btn"
                                onClick={() => changeMonth(-1)}
                                aria-label="Previous month"
                            >
                                <ChevronLeftIcon />
                            </button>
                            <button
                                type="button"
                                className="cmc-nav-btn"
                                onClick={() => changeMonth(1)}
                                aria-label="Next month"
                            >
                                <ChevronRightIcon />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="cmc-day-headers">
                    {WEEKDAYS_SHORT.map((name, index) => (
                        <div
                            key={name}
                            className={`cmc-day-header${index === 0 || index === 6 ? ' weekend' : ''}`}
                        >
                            {name}
                        </div>
                    ))}
                </div>

                <Dimmer.Dimmable as="div" className="cmc-grid-wrap" dimmed={loading}>
                    <Dimmer active={loading} inverted>
                        <Loader />
                    </Dimmer>
                    <DayPicker
                        month={visibleMonth}
                        onMonthChange={handleMonthChange}
                        selectedDays={selectedDate}
                        onDayClick={handleDayClick}
                        modifiers={{ today }}
                        showOutsideDays
                        enableOutsideDaysClick
                        firstDayOfWeek={0}
                        canChangeMonth={false}
                        classNames={dayPickerClassNames}
                        renderDay={(date) => (
                            <CompactDayContent date={date} eventsByDate={eventsByDate} />
                        )}
                    />
                </Dimmer.Dimmable>
            </div>

            <div className="cmc-agenda-panel">
                <div className="cmc-agenda-header">
                    <div className="cmc-agenda-day-label">Selected Day</div>
                    <div className="cmc-agenda-day-name">
                        {WEEKDAYS[selectedDate.getDay()]}, {MONTHS[selectedDate.getMonth()]}{' '}
                        {selectedDate.getDate()}
                    </div>
                    <div className="cmc-agenda-date-sub">
                        {selectedKey === todayKey ? 'Today' : '\u00a0'}
                    </div>
                    <div className="cmc-count-pill">
                        <CalendarSmallIcon />
                        {countLabel}
                    </div>
                </div>

                <div className="cmc-events-list">
                    {selectedDayEvents.length === 0 ? (
                        <div className="cmc-empty-state">
                            <div className="cmc-empty-icon">
                                <EmptyDayIcon />
                            </div>
                            <div className="cmc-empty-text">
                                No {pluralNoun} on this day.
                                <br />
                                Check back soon!
                            </div>
                        </div>
                    ) : (
                        selectedDayEvents.map((event, index) => {
                            const cardStyle: React.CSSProperties = {
                                animationDelay: `${0.05 + index * 0.06}s`,
                                ...(event.accentColor
                                    ? ({ '--cmc-accent': event.accentColor } as React.CSSProperties)
                                    : {}),
                            };
                            return (
                                <div
                                    key={event.occurrenceKey}
                                    className="cmc-event-card"
                                    style={cardStyle}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                        if (!loading) onEventClick(event);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (!loading) onEventClick(event);
                                        }
                                    }}
                                >
                                    {event.timeLabel && (
                                        <>
                                            <div className="cmc-event-time-col">
                                                <div className="cmc-event-time">{event.timeLabel}</div>
                                                {event.timeSuffix && (
                                                    <div className="cmc-event-ampm">
                                                        {event.timeSuffix}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="cmc-event-divider" />
                                        </>
                                    )}
                                    <div className="cmc-event-info">
                                        <div className="cmc-event-title">{event.title}</div>
                                        {event.subtitle && (
                                            <div className="cmc-event-meta">{event.subtitle}</div>
                                        )}
                                    </div>
                                    <div className="cmc-event-action">
                                        <CardChevronIcon />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompactMonthCalendar;
export * from './types';
